import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { DragPreviewImage } from 'react-dnd';
import { MediaDimensions } from '../../stores/types/types';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import { ContentTemplate, getTemplateDragType, StylePackContentType, templateLabels } from '../types/stylepacks';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { carouselSkele } from '../style-packs/skeleton-sizes';

interface ContentCardProps {
	url: string;
	thumbnail?: string;
	altText: string;
	slug?: string;
	provider?: string;
	customSize?: Required<MediaDimensions>;
	skeletonWidthHeight?: {width:number, height:number, style?:React.CSSProperties}
	contentType?: StylePackContentType;
	canDrag?: boolean;
	contentTemplate: ContentTemplate
}

export default function ContentCard({
	url,
	thumbnail,
	altText,
	customSize,
	slug,
	provider,
	contentType,
	contentTemplate,
	canDrag = true,
	skeletonWidthHeight,
}: ContentCardProps) {
	const [mouseTouched, setMouseTouched] = useState<boolean>(false);
	const [loaded, setLoaded] = useState(false);
	const pauseTimeout = useRef<NodeJS.Timeout>();

	const contentTypeText = useMemo(() => {
		if (contentType === StylePackContentType.TITLE) {
			return 'Lower';
		}
		const label = templateLabels[contentType as StylePackContentType];
		if (label === "Bookends" && slug) {
			// bespoke default name for bookend items
			if (slug?.indexOf('-intro-') >= 0) {
				return 'Intro';
			} else if (slug?.indexOf('-outro-') >= 0) {
				return 'Outro';
			}
		}
		return label;
	}, [contentType, templateLabels, slug]);

	const dndProps = {
		id: url,
		type: getTemplateDragType(contentType),
		name: `${altText} - ${contentTypeText}`,
		template: slug,
		provider,
		data: contentTemplate,
	};

	const videoRef = useRef<HTMLVideoElement>(null);
	const [thumbnailString, setThumbnailString] = useState<string>(thumbnail ?? '');
	const { dragRef, dragPreview } = useDragItem(dndProps, canDrag);

	const createImageObject = (imageUrl: string): Promise<HTMLImageElement> => new Promise(
		(resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.src = imageUrl;
			image.onload = () => resolve(image);
			image.onerror = reject;
		},
	);

	const createImageThumbnail = async (imageUrl: string) => {
		const image = await createImageObject(imageUrl);
		if (image) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			canvas.height = slug === 'brandlive-selfridges'
				? image.height * 0.75
				: image.height * 0.5;
			canvas.width = slug === 'brandlive-selfridges'
				? image.width * 0.75
				: image.width * 0.5;
			if (!ctx) {
				return thumbnail;
			}

			ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
			return canvas.toDataURL();
		}
		return undefined;
	};

	useEffect(() => {
		if (!thumbnail) return;
		createImageThumbnail(thumbnail)
			.then((base64) => {
				setThumbnailString(base64 || thumbnail);
				setLoaded(true);
			})
			.catch(() => {
				setThumbnailString(thumbnail);
				setLoaded(true);
			});
	}, [thumbnail]);

	if (!loaded) {
		return <Skeleton style={{
			marginRight: '8px',
			borderRadius: '8px',
			width: skeletonWidthHeight?.width ? skeletonWidthHeight.width: '100%',
			height: skeletonWidthHeight?.height ? skeletonWidthHeight.height: '100%',
			aspectRatio: '16/9',
			...skeletonWidthHeight?.style ?? {},
		}} />
	}

	return (
		<>
			<div
				className="content-card"
				style={{ cursor: canDrag ? 'grab' : 'default' }}
				data-testid="content-card"
				ref={dragRef}
				onMouseEnter={() => {
					clearTimeout(pauseTimeout.current);
					setMouseTouched(true);
				}}
				onMouseLeave={() => {
					pauseTimeout.current = setTimeout(() => {
						setMouseTouched(false);
					}, 1000);
				}}
			>
				<div className={classNames('image-container', { 'title': contentType === StylePackContentType.TITLE})}>
					<video
						className="no-select"
						style={customSize}
						src={mouseTouched ? url : ''}
						ref={videoRef}
						data-testid="content-card-video"
						autoPlay={true}
						poster={thumbnail}
						onLoadedData={() => setLoaded(true)}
						muted={true}
					/>
				</div>
			</div>
			{thumbnail && (
				<DragPreviewImage
					connect={dragPreview}
					src={thumbnailString}
				/>
			)}
		</>
	);
}
